import React, { useState, useEffect } from 'react';
import {
  Modal,
  Box,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Typography,
  Grid,
  TextField,
  FormControlLabel,
  Checkbox,
  IconButton,
  Tooltip
} from '@mui/material';
import DownloadIcon from '@mui/icons-material/Download';
import TableChartIcon from '@mui/icons-material/TableChart';
import * as XLSX from 'xlsx-js-style';
import { beautifyNumber } from '../../../Utils/FormatNumber';
import { getCaseNumberLabel } from '../../../Utils/ReportUtils';
import { buildReportWorkbook } from '../../../Utils/ExcelReport';
import { useTranslation } from 'react-i18next';
import ReportsService from '../../../Services/reports.service';
import { useHamburgerMenu } from '../../../Contexts/HamburgerMenuContext';
import { toast } from 'react-toastify';
import Constants from '../../../assests/Constants/constants.js';
/* ───────────── helpers ───────────── */
const cellFmt = (v) => {
  const formatted = beautifyNumber(v || 0);
  // Check if the formatted number contains parentheses (negative numbers)
  if (formatted.includes('(') && formatted.includes(')')) {
    return `<span style="color: #d32f2f">${formatted}</span>`;
  }
  return formatted;
};

const MonthlyReportModal = ({
  open,
  onClose,
  direction,
  t,
  // New props from LedgerReport
  palCodesData,
  palDates,
  trialBalanceData,
  client,
  token,
  userTypeContext
}) => {
  const { i18n } = useTranslation();

  // Chat functionality
  const { handleSendMessageToChat, selectUserAndOpenChat } = useHamburgerMenu();

  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
  const [startMonth, setStartMonth] = useState(1);
  const [endMonth, setEndMonth] = useState(12);
  const [monthlyReportHtml, setMonthlyReportHtml] = useState('');
  const [isGeneratingMonthlyReport, setIsGeneratingMonthlyReport] = useState(false);
  const [showWithZeros, setShowWithZeros] = useState(false);
  const [cachedMonthlyData, setCachedMonthlyData] = useState(null);
  // true → filter by document date, false → filter by registration month (same as LedgerReport)
  const [filterByDocumentDate, setFilterByDocumentDate] = useState(() => {
    const stored = localStorage.getItem('ledgerReportFilterByDocumentDate');
    return stored === null ? true : stored === 'true';
  });

  const handleDateBasisChange = (byDocumentDate) => {
    if (byDocumentDate === filterByDocumentDate) return;
    setFilterByDocumentDate(byDocumentDate);
    localStorage.setItem('ledgerReportFilterByDocumentDate', byDocumentDate);
  };

  const handleGenerate = async () => {
    const reportData = {
      year: selectedYear,
      startMonth: startMonth,
      endMonth: endMonth
    };

    try {
      setIsGeneratingMonthlyReport(true);

      // Fetch data for the selected year
      const monthlyData = await fetchMonthlyData(reportData);

      // Cache the data for potential regeneration
      setCachedMonthlyData(monthlyData);

      // Generate the monthly report HTML with the fetched data
      generateMonthlyReportHtml(reportData, monthlyData);

      // Call the parent handler
    } catch (error) {
      console.error('Error generating monthly report:', error);
    } finally {
      setIsGeneratingMonthlyReport(false);
    }
  };

  // Regenerate report when showWithZeros changes
  const handleShowWithZerosChange = (checked) => {
    setShowWithZeros(checked);
    // If report is already generated, regenerate it with new filter
    if (monthlyReportHtml && cachedMonthlyData) {
      const reportData = {
        year: selectedYear,
        startMonth: startMonth,
        endMonth: endMonth
      };
      // Regenerate the HTML with the cached data and new filter
      generateMonthlyReportHtml(reportData, cachedMonthlyData, checked);
    }
  };

  const handleClose = () => {
    setMonthlyReportHtml('');
    setCachedMonthlyData(null);
    onClose();
  };

  // Fetch monthly data from API
  const fetchMonthlyData = async (reportData) => {
    const { year, startMonth, endMonth } = reportData;

    // Create start and end dates for the selected year
    const startDate = new Date(Date.UTC(year, startMonth - 1, 1));
    const endDate = new Date(Date.UTC(year, endMonth, 0)); // Last day of end month

    try {
      // Fetch palCodes data for the selected year
      const palCodesResponse = await ReportsService.GetPalCodesDetails(
        client.caseId,
        client.accountantId,
        startDate,
        endDate,
        false, // isAnnual - false for monthly periods
        token,
        true, // onlyProcessed
        true,  // isProfitAndLoss
        filterByDocumentDate
      );

      if (!palCodesResponse.ok) {
        const errorText = await palCodesResponse.text();
        throw new Error(`Failed to fetch palCodes data: ${errorText}`);
      }

      const palCodesData = await palCodesResponse.json();

      // Fetch trial balance data for the selected year
      const trialBalanceResponse = await ReportsService.GetTrialBalanceDetails(
        client.caseId,
        client.accountantId,
        startDate,
        endDate,
        false, // isAnnual - false for monthly periods
        token,
        true, // onlyProcessed
        true,  // isProfitAndLoss
        null, // trialBalanceFilter
        filterByDocumentDate
      );

      if (!trialBalanceResponse.ok) {
        const errorText = await trialBalanceResponse.text();
        throw new Error(`Failed to fetch trial balance data: ${errorText}`);
      }

      const trialBalanceData = await trialBalanceResponse.json();

      // Extract dates from the palCodes data
      const dates = new Set();
      palCodesData.palCodes.forEach(palCode => {
        palCode.accountCards.forEach(accountCard => {
          accountCard.dateAmounts.forEach(dateAmount => {
            dates.add(dateAmount.date);
          });
        });
      });

      const sortedDates = Array.from(dates).sort((a, b) => new Date(a) - new Date(b));

      return {
        palCodesData: palCodesData.palCodes,
        palDates: sortedDates,
        trialBalanceData: trialBalanceData
      };
    } catch (error) {
      console.error('Error fetching monthly data:', error);
      throw error;
    }
  };

  // Generate monthly report HTML similar to ProfitAndLossReportHtml
  const buildMonthlyTableColGroup = (monthKeys) => {
    const monthCols = monthKeys.map(() => '<col class="col-month" />').join('');
    return `<colgroup><col class="col-description" />${monthCols}<col class="col-total" /></colgroup>`;
  };

  const generateMonthlyReportHtml = (reportData, monthlyData, showWithZerosOverride = null) => {
    if (!monthlyData || !monthlyData.palCodesData || !monthlyData.palDates) return;

    // Use the override value if provided, otherwise use the current state
    const currentShowWithZeros = showWithZerosOverride !== null ? showWithZerosOverride : showWithZeros;

    // Filter data for the selected year and months
    const filteredData = filterDataForMonthlyReport(reportData, monthlyData, currentShowWithZeros);

    // Generate header
    const headerContent = generateMonthlyReportHeader(reportData);

    // Generate table content (for display in modal - no page breaks)
    const tableContent = generateMonthlyReportTable(filteredData, reportData, false);

    // Combine content
    const fullContent = headerContent + tableContent;

    // Generate full HTML document
    const reportHtml = generateFullHtmlDocument(fullContent);
    setMonthlyReportHtml(reportHtml);
  };

  // Filter data for the selected year and months
  const filterDataForMonthlyReport = (reportData, monthlyData, showWithZerosOverride = null) => {
    const { year, startMonth, endMonth } = reportData;
    const { palCodesData, palDates } = monthlyData;

    // Use the override value if provided, otherwise use the current state
    const currentShowWithZeros = showWithZerosOverride !== null ? showWithZerosOverride : showWithZeros;

    // We can filter later once we confirm the data is showing
    const allDates = palDates.filter(date => date !== 'InitialBalance');

    // Apply showWithZeros filtering logic
    let filteredPalCodes = palCodesData;
    if (!currentShowWithZeros) {
      // When showWithZeros is false (default), filter out palCodes that have all zero amounts
      filteredPalCodes = palCodesData.filter(palCode => {
        return palCode.accountCards.some(accountCard =>
          accountCard.dateAmounts.some(dateAmount => dateAmount.amount !== 0)
        );
      });
    }
    // When showWithZeros is true, show all palCodes (including those with zeros)

    return {
      palCodes: filteredPalCodes, // Use filtered palCodes data
      monthKeys: allDates, // Use all dates from API for debugging
      year,
      startMonth,
      endMonth
    };
  };

  // Generate header for monthly report
  const createMonthlyReportDateTime = () => {
    const now = new Date();
    const currentTime = now.toLocaleTimeString('he-IL', {
      hour: '2-digit',
      minute: '2-digit',
      hour12: false,
    });
    const day = now.getDate().toString().padStart(2, '0');
    const month = (now.getMonth() + 1).toString().padStart(2, '0');
    const currentYear = now.getFullYear().toString().slice(-2);
    const currentDate = `${day}/${month}/${currentYear}`;

    return { currentDate, currentTime };
  };

  const generateMonthlyReportHeader = (reportData) => {
    const { year, startMonth, endMonth } = reportData;
    const { currentDate, currentTime } = createMonthlyReportDateTime();
    const clientName = client?.name || client?.clientName || '';
    const clientCase = client?.caseId || client?.vatClientNumber || '';
    const paddedStartMonth = String(startMonth).padStart(2, '0');
    const paddedEndMonth = String(endMonth).padStart(2, '0');

    return `
      <div class="report-page-header pageStyle">
        <div class="report-header-row">
          <div class="report-header-title-block">
            <h1 class="report-title">${t('MonthlyProfitAndLossReport')}</h1>
            <p class="report-date-range">מחודש ${paddedStartMonth}/${year} עד חודש ${paddedEndMonth}/${year}</p>
          </div>
          <div class="report-header-client-block">
            <p class="report-client-name">${clientName}</p>
            <p class="report-client-case">${getCaseNumberLabel(client?.caseType)}: ${clientCase}</p>
            <p class="report-print-datetime">תאריך הדפסה: ${currentTime} - ${currentDate}</p>
          </div>
        </div>
        <hr class="report-header-divider" />
      </div>
    `;
  };

  const buildPrintPageContinuation = (reportData, generateTableHeaders) => `
          </tbody>
          </table>
          <div class="page-break"></div>
          ${generateMonthlyReportHeader(reportData)}
          <div class="table-spacing"></div>
          <table class="table" dir="rtl">
            <thead>
              ${generateTableHeaders()}
            </thead>
            <tbody>
        `;

  // Generate the main table for monthly report - displaying all data without expand/collapse
  const generateMonthlyReportTable = (filteredData, reportData, isForPrint = false) => {
    const { palCodes, monthKeys } = filteredData;



    // Format dates like TrialBalanceTable does
    const formatDateForHeader = (date) => {
      if (date === 'InitialBalance') return t('InitialBalance');
      const dateObj = new Date(date);
      return dateObj.toLocaleDateString('he-IL', {
        month: '2-digit',
        year: 'numeric'
      }).replace('/', '/');
    };

    // Generate table headers HTML
    const generateTableHeaders = () => {
      return `
        <tr class="table-header-row">
          <th class="td-header">${t('Description')}</th>
          ${monthKeys.map(date =>
        `<th class="td-header">${formatDateForHeader(date)}</th>`
      ).join('')}
          <th class="td-header" >${t('Total')}</th>
        </tr>
      `;
    };

    // Generate table content - different logic for print vs display
    let tableContent = `
      <table class="table" dir="rtl">
        ${!isForPrint ? buildMonthlyTableColGroup(monthKeys) : ''}
        <thead>
          ${generateTableHeaders()}
        </thead>
        <tbody>
    `;

    // Process each PAL code and display all data
    let rowCount = 0;
    const rowsPerPage = 15; // Adjust this number based on your page size

    palCodes.forEach((palCode, index) => {
      // Calculate PAL code totals (same logic as TrialBalanceTable)
      const palCodeTotal = palCode.accountCards.reduce((sum, accountCard) => {
        return sum + accountCard.dateAmounts.reduce(
          (acc, dateAmount) => acc + (dateAmount ? dateAmount.amount : 0),
          0
        );
      }, 0);

      // Calculate monthly totals for PAL code
      const monthlyTotals = monthKeys.map(date => {
        return palCode.accountCards.reduce((sum, accountCard) => {
          const dateAmount = accountCard.dateAmounts.find(da => da.date === date);
          return sum + (dateAmount ? dateAmount.amount : 0);
        }, 0);
      });

      // Check if we need a page break before this PAL code (only for print)
      if (isForPrint && rowCount > 0 && rowCount % rowsPerPage === 0) {
        tableContent += buildPrintPageContinuation(reportData, generateTableHeaders);
      }

      // Add PAL code row (parent row) - always visible
      tableContent += `
        <tr class="tr-palcode-parent">
          <td class="td-description">
            <strong>${palCode.palCodeId === 'NonPalCode' ? t('NonPalCode') : palCode.palCodeId}</strong>
          </td>
          ${monthlyTotals.map(total =>
        `<td class="td-amount"><strong>${cellFmt(total)}</strong></td>`
      ).join('')}
          <td class="td-total word-wrap"><strong>${cellFmt(palCodeTotal)}</strong></td>
        </tr>
      `;
      rowCount++;

      // Add ALL account cards rows (child rows) - always visible
      palCode.accountCards.forEach((accountCard, cardIndex) => {
        // Check if we need a page break before this account card (only for print)
        if (isForPrint && rowCount > 0 && rowCount % rowsPerPage === 0) {
          tableContent += buildPrintPageContinuation(reportData, generateTableHeaders);
        }

        // Calculate account card totals
        const accountCardTotal = accountCard.dateAmounts.reduce(
          (sum, dateAmount) => sum + (dateAmount ? dateAmount.amount : 0),
          0
        );

        // Calculate monthly totals for account card
        const cardMonthlyTotals = monthKeys.map(date => {
          const dateAmount = accountCard.dateAmounts.find(da => da.date === date);
          return dateAmount ? dateAmount.amount : 0;
        });

        tableContent += `
          <tr class="tr-account-card">
            <td class="td-description-child">
              ${accountCard.name}
            </td>
            ${cardMonthlyTotals.map(total =>
          `<td class="td-amount">${cellFmt(total)}</td>`
        ).join('')}
            <td class="td-total word-wrap">${cellFmt(accountCardTotal)}</td>
          </tr>
        `;
        rowCount++;
      });
    });

    // Close the current table (display keeps footer in same table for column alignment)
    if (isForPrint) {
      tableContent += `
      </tbody>
      </table>
    `;

      const footerTotals = calculateFooterTotals(palCodes, monthKeys);

      tableContent += `
        <div class="footer-container" style="margin-top: 40px;">
          <table class="table footer-table" dir="rtl">
            <tbody>
              <tr class="tr-footer">
                <td class="td-footer"><strong>${t('ProfitLossForPeriod')}</strong></td>
                ${monthKeys.map(date =>
        `<td class="td-footer"><strong>${cellFmt(footerTotals.dateTotals[date] || 0)}</strong></td>`
      ).join('')}
                <td class="td-footer word-wrap"><strong>${cellFmt(footerTotals.grandTotal)}</strong></td>
              </tr>
            </tbody>
          </table>
        </div>
      `;
    } else {
      const footerTotals = calculateFooterTotals(palCodes, monthKeys);

      tableContent += `
      </tbody>
      </table>
      <div class="footer-container footer-container-screen">
        <table class="table footer-table" dir="rtl">
          ${buildMonthlyTableColGroup(monthKeys)}
          <tbody>
            <tr class="tr-footer">
              <td class="td-footer"><strong>${t('ProfitLossForPeriod')}</strong></td>
              ${monthKeys.map(date =>
        `<td class="td-footer"><strong>${cellFmt(footerTotals.dateTotals[date] || 0)}</strong></td>`
      ).join('')}
              <td class="td-footer word-wrap"><strong>${cellFmt(footerTotals.grandTotal)}</strong></td>
            </tr>
          </tbody>
        </table>
      </div>
      `;
    }

    return tableContent;
  };

  // Calculate footer totals like TrialBalanceTable
  const calculateFooterTotals = (palCodes, monthKeys) => {
    const totals = {
      grandTotal: 0,
      dateTotals: monthKeys.reduce((acc, date) => {
        acc[date] = 0;
        return acc;
      }, {})
    };

    palCodes.forEach(palCode => {
      const palCodeTotal = palCode.accountCards.reduce((sum, accountCard) => {
        return sum + accountCard.dateAmounts.reduce((acc, dateAmount) => acc + (dateAmount ? dateAmount.amount : 0), 0);
      }, 0);

      totals.grandTotal += palCodeTotal;

      monthKeys.forEach(date => {
        const dateTotal = palCode.accountCards.reduce((sum, accountCard) => {
          const dateAmount = accountCard.dateAmounts.find(da => da.date === date);
          return sum + (dateAmount ? dateAmount.amount : 0);
        }, 0);
        totals.dateTotals[date] += dateTotal;
      });
    });

    return totals;
  };

  // Generate print version with page breaks
  const generatePrintVersion = (reportData, monthlyData, showWithZerosOverride = null) => {
    if (!monthlyData || !monthlyData.palCodesData || !monthlyData.palDates) return '';

    // Use the override value if provided, otherwise use the current state
    const currentShowWithZeros = showWithZerosOverride !== null ? showWithZerosOverride : showWithZeros;

    // Filter data for the selected year and months
    const filteredData = filterDataForMonthlyReport(reportData, monthlyData, currentShowWithZeros);

    // Generate header
    const headerContent = generateMonthlyReportHeader(reportData);

    // Generate table content with page breaks for print
    const tableContent = generateMonthlyReportTable(filteredData, reportData, true);

    // Combine content
    const fullContent = headerContent + tableContent;

    // Generate full HTML document
    return generateFullHtmlDocument(fullContent, true);
  };

  // Generate full HTML document with styles
  const generateFullHtmlDocument = (content, forPrint = false) => {
    return `
      <!DOCTYPE html>
      <html lang="he">
      <head>
          <meta charset="UTF-8">
          <meta name="viewport" content="width=device-width, initial-scale=1.0">
          <style>
          ${getReportStyles(forPrint)}
          </style>
      </head>
      <body class="monthly-report-modal${forPrint ? ' monthly-report-print' : ' monthly-report-display'}">
          <div class="content-wrapper">
              ${content}
          </div>
      </body>
      </html>
    `;
  };

  // Handle Excel download
  const handleDownloadExcel = () => {
    try {
      if (!cachedMonthlyData) {
        console.error('No cached data available for Excel generation');
        return;
      }

      const reportData = {
        year: selectedYear,
        startMonth: startMonth,
        endMonth: endMonth
      };

      const filteredData = filterDataForMonthlyReport(reportData, cachedMonthlyData, showWithZeros);
      const { palCodes, monthKeys } = filteredData;

      const formatDateForHeader = (date) => {
        if (date === 'InitialBalance') return t('InitialBalance');
        const dateObj = new Date(date);
        return dateObj.toLocaleDateString('he-IL', {
          month: '2-digit',
          year: 'numeric'
        }).replace('/', '/');
      };

      const getMonthlyTotals = (accountCards) =>
        monthKeys.map((date) =>
          accountCards.reduce((sum, accountCard) => {
            const dateAmount = accountCard.dateAmounts.find((da) => da.date === date);
            return sum + (dateAmount ? dateAmount.amount : 0);
          }, 0)
        );

      const getTotal = (accountCards) =>
        accountCards.reduce(
          (sum, accountCard) =>
            sum +
            accountCard.dateAmounts.reduce(
              (acc, dateAmount) => acc + (dateAmount ? dateAmount.amount : 0),
              0
            ),
          0
        );

      const round2 = (v) => Math.round((Number(v) || 0) * 100) / 100;

      const header = [t('Description'), ...monthKeys.map(formatDateForHeader), t('Total')];
      const rows = [];

      palCodes.forEach((palCode) => {
        const monthlyTotals = getMonthlyTotals(palCode.accountCards);
        const palCodeTotal = getTotal(palCode.accountCards);
        const palCodeLabel =
          palCode.palCodeId === 'NonPalCode' ? t('NonPalCode') : palCode.palCodeId;

        rows.push([palCodeLabel, ...monthlyTotals.map(round2), round2(palCodeTotal)]);

        palCode.accountCards.forEach((accountCard) => {
          const cardMonthlyTotals = monthKeys.map((date) => {
            const dateAmount = accountCard.dateAmounts.find((da) => da.date === date);
            return dateAmount ? dateAmount.amount : 0;
          });
          const accountCardTotal = accountCard.dateAmounts.reduce(
            (sum, dateAmount) => sum + (dateAmount ? dateAmount.amount : 0),
            0
          );

          rows.push([accountCard.name, ...cardMonthlyTotals.map(round2), round2(accountCardTotal)]);
        });
      });

      const footerTotals = calculateFooterTotals(palCodes, monthKeys);
      rows.push([
        t('ProfitLossForPeriod'),
        ...monthKeys.map((date) => round2(footerTotals.dateTotals[date] || 0)),
        round2(footerTotals.grandTotal)
      ]);

      const clientName = client?.name || client?.clientName || 'Report';
      const clientCase = client?.caseId || '';

      // First column is the description; every remaining column is a numeric amount.
      const numericColIndexes = header.map((_, idx) => idx).filter((idx) => idx > 0);
      const colWidths = header.map((_, idx) => (idx === 0 ? 34 : idx === header.length - 1 ? 16 : 14));

      const wb = buildReportWorkbook({
        sheetName: 'Monthly P&L',
        titleLines: [clientName, t('MonthlyProfitAndLossReport') || t('ProfitAndLossReport')].filter(Boolean),
        header,
        rows,
        colWidths,
        numericColIndexes,
      });
      XLSX.writeFile(wb, `${clientName}-${clientCase}_MonthlyProfitAndLossReport.xlsx`);
    } catch (error) {
      console.error('Error downloading Excel:', error);
    }
  };

  // Handle PDF download
  const handleDownloadPdf = async () => {
    try {
      if (!cachedMonthlyData) {
        console.error('No cached data available for PDF generation');
        return;
      }

      const reportData = {
        year: selectedYear,
        startMonth: startMonth,
        endMonth: endMonth
      };

      // Generate the print version HTML for PDF
      const printHtml = generatePrintVersion(reportData, cachedMonthlyData, showWithZeros);

      // Clean the HTML for PDF generation (remove interactive elements)
      const cleanedHtml = printHtml
        .replace(/border: 1px solid #ccc;/g, '')
        .replace(/box-shadow: 0px 0px 10px rgba\(0, 0, 0, 0\.1\);/g, '')
        .replace(/\s*class="hoverTr"/g, '')
        .replace(/\s*hoverTr\s*/g, '');

      const { blob, fileName } = await ReportsService.convertHtmlToPdf(client?.caseId, cleanedHtml, token);

      // Create download link
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = fileName;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Error downloading PDF:', error);
    }
  };

  // Chat functionality
  const handleSendToChat = async () => {
    try {
      if (!cachedMonthlyData) {
        console.error('No cached data available for chat');
        return;
      }

      // First, open chat with the client
      await selectUserAndOpenChat(client.caseId, client.name || 'Client');

      const reportData = {
        year: selectedYear,
        startMonth: startMonth,
        endMonth: endMonth
      };

      // Generate the print version HTML for PDF (same as handleDownloadPdf)
      const printHtml = generatePrintVersion(reportData, cachedMonthlyData, showWithZeros);

      // Clean the HTML for PDF generation (remove interactive elements)
      const cleanedHtml = printHtml
        .replace(/border: 1px solid #ccc;/g, '')
        .replace(/box-shadow: 0px 0px 10px rgba\(0, 0, 0, 0\.1\);/g, '')
        .replace(/\s*class="hoverTr"/g, '')
        .replace(/\s*hoverTr\s*/g, '');

      const { blob, fileName } = await ReportsService.convertHtmlToPdf(client?.caseId, cleanedHtml, token);

      // Create a file from the blob
      const file = new File([blob], fileName || `${client.name}-${client.caseId}_MonthlyReport.pdf`, { type: 'application/pdf' });

      // Send the file to chat
      await handleSendMessageToChat(file, client.caseId, client.name);
      toast.success(t('FileSentToChat'));
    } catch (error) {
      console.error('Error sending monthly report to chat:', error);
      toast.error(t('ErrorSendingToChat'));
    }
  };

  // Get CSS styles for the monthly report - with maximum specificity to override other styles
  const getReportStyles = (forPrint = false) => {
    return `
      /* Monthly Report Modal Styles - Maximum specificity to override conflicting styles */
      /* Complete CSS Reset for Monthly Report */
      * {
        all: unset !important;
        display: revert !important;
        box-sizing: border-box !important;
      }
      
      html, body {
        margin: 0 !important;
        padding: 0 !important;
        font-family: Arial, sans-serif !important;
        direction: rtl !important;
      }
      
      .monthly-report-modal * {
        box-sizing: border-box !important;
        font-family: Arial, sans-serif !important;
      }
      
      .monthly-report-modal body {
        margin: 40px 10px 10px 10px !important;
        padding: 10px !important;
        direction: rtl !important;
        font-family: Arial, sans-serif !important;
      }
      
      .monthly-report-modal .content-wrapper {
        border: 2px solid #0066cc !important;
        border-radius: 8px !important;
        padding: 10px !important;
        margin: 0 !important;
      }

      .monthly-report-modal .pageStyle {
        display: block;
        background: #ffffff;
        margin: 0 0 8px 0;
        page-break-inside: avoid;
      }

      .monthly-report-modal .report-page-header {
        margin-bottom: 4px;
      }

      .monthly-report-modal .report-header-row {
        display: flex !important;
        flex-direction: row !important;
        direction: rtl !important;
        justify-content: space-between !important;
        align-items: flex-start !important;
        gap: 16px !important;
        padding: 4px 0 !important;
        width: 100% !important;
      }

      .monthly-report-modal .report-header-title-block {
        display: flex !important;
        flex-direction: column !important;
        align-items: flex-start !important;
        direction: rtl !important;
        text-align: right !important;
        flex: 1 !important;
      }

      .monthly-report-modal .report-title {
        margin: 0 0 6px 0 !important;
        padding: 0 !important;
        font-size: 24px !important;
        font-weight: 700 !important;
        color: #000 !important;
        line-height: 1.2 !important;
        text-decoration: none !important;
        text-align: right !important;
        width: 100% !important;
      }

      .monthly-report-modal .report-date-range {
        margin: 0 !important;
        padding: 0 !important;
        font-size: 13px !important;
        font-weight: 600 !important;
        color: #222 !important;
        text-decoration: none !important;
        text-align: right !important;
        width: 100% !important;
      }

      .monthly-report-modal .report-header-client-block {
        text-align: left !important;
        direction: rtl !important;
        flex: 1 !important;
        max-width: 45% !important;
        align-self: flex-start !important;
      }

      .monthly-report-modal .report-client-name {
        margin: 0 0 4px 0 !important;
        padding: 0 !important;
        font-size: 15px !important;
        font-weight: 700 !important;
        color: #000 !important;
        text-align: left !important;
      }

      .monthly-report-modal .report-client-case,
      .monthly-report-modal .report-print-datetime {
        margin: 0 0 2px 0 !important;
        padding: 0 !important;
        font-size: 13px !important;
        font-weight: 600 !important;
        color: #333 !important;
        text-align: left !important;
      }

      .monthly-report-modal .report-header-divider {
        border: none !important;
        border-top: 2px solid #000 !important;
        margin: 4px 0 8px !important;
        width: 100% !important;
      }

      .monthly-report-modal .table-spacing {
        height: 8px;
      }

      
      .monthly-report-modal .upper-name-div {
        display: -webkit-box !important;              /* Old syntax for older WebKit browsers */
        display: -moz-box !important;                 /* Old syntax for Firefox */
        display: -ms-flexbox !important;              /* Old syntax for IE10 */
        display: -webkit-flex !important;             /* Newer WebKit syntax */
        display: flex !important;                     /* Standard syntax */
        
        -webkit-box-orient: horizontal !important;    /* Ensures horizontal layout in older WebKit */
        -moz-box-orient: horizontal !important;       /* Ensures horizontal layout in older Firefox */
        -webkit-flex-direction: row !important;       /* Flexbox row direction for WebKit */
        -ms-flex-direction: row !important;           /* Flexbox row direction for IE10 */
        flex-direction: row !important;                /* Standard syntax */
        direction: rtl !important;  
        justify-content: space-between !important;    /* Space between left and right content */
        align-items: flex-start !important;           /* Align items to top */
        padding: 5px !important;
        border-radius: 5px !important;
        margin-bottom: 0px !important;
      }
      
      .monthly-report-modal .left-header-info {
        display: flex !important;
        flex-direction: row !important;
        align-items: flex-start !important;
        text-align: left !important;
        direction: ltr !important;
      }
      
      .monthly-report-modal .right-header-info {
        display: flex !important;
        flex-direction: row !important;
        align-items: flex-end !important;
        text-align: right !important;
        direction: rtl !important;
      }
      
      .monthly-report-modal .current-datetime {
        font-size: 11px !important;
        color: #333 !important;
        font-weight: bold !important;
        direction: ltr !important;
      }

      .monthly-report-modal .first {
        margin: 0 !important;
        padding: 0 !important;
        padding-bottom: 10px !important;
        font-size: 12px !important;
        font-style: normal !important;
        font-weight: 800 !important;
        line-height: normal !important;
      }

      /* Header positioning for print - similar to AccountCardReportHTML */
      .monthly-report-modal #header {
        position: fixed !important;
        top: 0px !important;
        left: 0px !important;
        width: 100% !important;
        z-index: 9999 !important;
      }
      
      .monthly-report-modal .middle-details {
        display: flex !important;
        flex-direction: column !important;
        align-items: center !important;
        justify-content: center !important;
        padding: 10px !important;
        text-align: center !important;
      }
      
      .monthly-report-modal .rt1 {
        font-size: 20px !important;
        font-weight: 800 !important;
        text-decoration: underline !important;
        margin-top: 24px !important;
      }
      
      .monthly-report-modal .det-small {
        font-size: 16px !important;
        text-decoration: underline !important;
        font-weight: 900 !important;
      }
      
      .monthly-report-modal .table {
        width: 100% !important;
        border-collapse: collapse !important;
        
        direction: rtl !important;
      }
      
      .monthly-report-modal .table th {
        background-color: rgb(217, 221, 249) !important;
        color: #304FFF !important;
        font-weight: bold !important;
        padding: 10px 8px !important;
        border: 1px solid #ddd !important;
        font-size: 14px !important;
      }
      
      .monthly-report-modal .table td {
        border: 1px solid #ddd !important;
        padding: 4px !important;
        text-align: right !important;
        font-size: 12px !important;
      }
      
      .monthly-report-modal .td-header {
        background-color: rgb(217, 221, 249) !important;
        color: #304FFF !important;
        font-weight: bold !important;
        padding: 10px 8px !important;
        border: 1px solid #ddd !important;
        font-size: 14px !important;
      }
      
      .monthly-report-modal .td-description {
        text-align: right !important;
        padding: 8px 8px !important;
        margin-right: 5px !important;
        border: 0.5px solid #e0e0e0 !important;
        font-weight: 300 !important;
        word-wrap: break-word !important;
        width: 220px !important;
      }
      
      .monthly-report-modal .td-description-child {
        text-align: right !important;
        padding: 8px 8px 8px 24px !important;
        margin-right: 5px !important;
        border: 0.5px solid #e0e0e0 !important;
        font-weight: 300 !important;
        background-color: #ffffff !important;
      }
      
      .monthly-report-modal .tr-palcode-parent {
        background-color: #f7f7f7 !important;
        border-bottom: 2px solid #000 !important;
        font-weight: 300 !important;
      }
      
      .monthly-report-modal .tr-palcode-parent:hover {
        background-color: rgba(0, 0, 0, 0.04) !important;
      }
      
      .monthly-report-modal .tr-account-card {
        background-color: #ffffff !important;
      }
      
      .monthly-report-modal .tr-account-card:hover {
        background-color: rgba(0, 0, 0, 0.02) !important;
      }
      
      .monthly-report-modal .tr-footer {
        background: #e8e8e8 !important;
        border-top: 2px solid #000 !important;

      }
      
      .monthly-report-modal .td-footer {
        background: rgb(251, 251, 251) !important;
        text-align: right !important;
        margin-left: 5px !important;
        font-weight: bold !important;
        padding: 8px 4px !important;
        border: 0.2px solid #e0e0e0 !important;
      }
      
      /* Add visual separation between PAL code groups */
      .monthly-report-modal .tr-palcode-parent + .tr-account-card:first-of-type {
        border-top: 1px solid #ccc !important;
      }
      
      /* Style for better readability */
      .monthly-report-modal .td-amount {
        text-align: right !important;
        padding: 8px 4px !important;
        font-family: Arial, sans-serif !important;
        font-size: 10px !important;
        font-weight: 400 !important;
      }
      
      .monthly-report-modal .td-total {
        font-weight: bold !important;
        text-align: right !important;
        padding: 8px 4px !important;
        font-family: Arial, sans-serif !important;
        font-size: 10px !important;
      }
      
      /* Ensure span styles work in iframe */
      .monthly-report-modal span {
        color: inherit !important;
      }
      
      .monthly-report-modal span[style*="color: #d32f2f"] {
        color: #d32f2f !important;
        font-weight: bold !important;
      }

      ${forPrint ? '' : `
      @media screen {
        .monthly-report-display .table,
        .monthly-report-display .footer-table {
          table-layout: fixed !important;
          width: 100% !important;
        }

        .monthly-report-display col.col-description {
          width: 220px !important;
        }

        .monthly-report-display col.col-total {
          width: 90px !important;
        }

        .monthly-report-display .footer-table .td-footer:first-child {
          width: 220px !important;
          min-width: 220px !important;
          max-width: 220px !important;
        }

        .monthly-report-display .footer-table .td-footer {
          margin-left: 0 !important;
        }

        .monthly-report-display .tr-footer {
          position: sticky !important;
          bottom: 0 !important;
        }
      }
      `}
      
      @media print {
          @page {
            /* Pick one: A4/Letter, both shown */
            size: A4 landscape;         /* for A4 */
            /* size: Letter landscape;  /* for US Letter */
            margin: 10mm 4mm 4mm 4mm;
          }

          .word-wrap{
            word-wrap: break-word !important;
            white-space: normal !important;
            overflow: visible !important;
            text-overflow: unset !important;
          }
          
          /* Ensure table structure is maintained for printing */
          .monthly-report-modal .table {
            table-layout: fixed !important;
            width: 100% !important;
            border-collapse: collapse !important;
            page-break-inside: auto !important;
            margin-top: 10px !important;
            max-width: 100% !important;
          }

          .monthly-report-modal .table td {
            border: 1px solid #ddd !important;
            padding: 4px !important;
            text-align: right !important;
            font-size: 11px !important;
          }
          
          /* Set specific column widths for print */
          .monthly-report-modal .table th:first-child,
          .monthly-report-modal .table td:first-child {
            width: 95px !important; /* Description column - further reduced */
            min-width: 95px !important;
            max-width: 95px !important;
          }
          
          .monthly-report-modal .table th:not(:first-child):not(:last-child),
          .monthly-report-modal .table td:not(:first-child):not(:last-child) {
            width: 70px !important; /* Month columns - reduced for better fit */
            min-width: 70px !important;
            max-width: 70px !important;
          }
          
          .monthly-report-modal .table th:last-child,
          .monthly-report-modal .table td:last-child {
            width: 70px !important; /* Total column - reduced for better fit */
            min-width: 70px !important;
            max-width: 70px !important;
          }
          
          /* Force table to maintain structure */
          .monthly-report-modal .table,
          .monthly-report-modal .table * {
            -webkit-print-color-adjust: exact !important;
            color-adjust: exact !important;
          }
          @page {
              @top-left {
                  content: "עמוד " counter(page) " מתוך " counter(pages);
                  font-size: 10px;
                  font-weight: 800;
                  color: #333;
                  margin-left: 5px;
              }
          }
          
          .page-break {
              page-break-before: always !important;
              break-before: page !important;
          }

          .monthly-report-modal .report-page-header {
            page-break-inside: avoid !important;
            break-inside: avoid !important;
            margin-bottom: 6px !important;
          }

          .monthly-report-modal .report-title {
            font-size: 20px !important;
          }

          .monthly-report-modal .report-date-range {
            font-size: 12px !important;
          }

          .monthly-report-modal .report-client-name {
            font-size: 13px !important;
          }

          .monthly-report-modal .report-client-case,
          .monthly-report-modal .report-print-datetime {
            font-size: 11px !important;
          }

          .monthly-report-modal .report-header-divider {
            margin: 4px 0 6px !important;
          }
          
          /* Style for manual table headers */
          .monthly-report-modal .table-header-row {
            background-color: rgb(217, 221, 249) !important;
            color: #304FFF !important;
            font-weight: bold !important;
            text-align: center !important;
            padding: 8px 6px !important;
            border: 1px solid #ddd !important;
            font-size: 14px !important;
            -webkit-print-color-adjust: exact !important;
            color-adjust: exact !important;
          }
          
          .monthly-report-modal .table-header-row th {
            background-color: rgb(217, 221, 249) !important;
            color: #304FFF !important;
            font-weight: bold !important;
            text-align: center !important;
            padding: 8px 6px !important;
            border: 1px solid #ddd !important;
            font-size: 12px !important;
            -webkit-print-color-adjust: exact !important;
            color-adjust: exact !important;
          }
          
          .page-number::before {
              content: counter(page);
          }
          
          .total-pages::before {
              content: counter(pages);
          }
          
          body {
              counter-reset: page;
              
          }
      
        .monthly-report-modal body {
          margin: 0 !important;
          width: 100% !important;
          max-width: 100% !important;
        }
        .monthly-report-modal .content-wrapper {
          border: none !important;
          padding: 0 !important;
          width: 100% !important;
          max-width: 100% !important;
        }
        
        /* Overall page width and size controls */
        .monthly-report-modal {
          width: 100% !important;
          max-width: 100% !important;
          overflow: hidden !important;
        }
        
        
        /* Add spacing for page breaks to account for header */
        .monthly-report-modal .page-break {
          margin-top: 10px !important;
        }
        
        /* Add spacing after page breaks to ensure content starts after header */
        .monthly-report-modal .table-spacing {
          height: 20px !important;
          margin: 0 !important;
          padding: 0 !important;
        }

        .monthly-report-modal #header {
            position: fixed !important;
            top: 0px !important;
            left: 0px !important;
            padding: 2px !important;
            width: 100% !important;
            z-index: 1000 !important;
            -webkit-print-color-adjust: exact !important;
            color-adjust: exact !important;
            background-color: none !important;
          }

          /* Force table headers to repeat on each page - Maximum specificity */
          .monthly-report-modal .table {
            border-collapse: collapse !important;
            table-layout: fixed !important;
            width: 100% !important;
          }
          
          .monthly-report-modal .table thead {
            display: table-header-group !important;
            position: relative !important;
            z-index: 10 !important;
          }
          
          .monthly-report-modal .table thead tr {
            display: table-row !important;
            page-break-inside: avoid !important;
            break-inside: avoid !important;
          }
          .monthly-report-modal .table th {
        t
          .monthly-report-modal .table thead th,
          .monthly-report-modal .table thead .td-header {
            display: table-cell !important;
            page-break-inside: avoid !important;
            break-inside: avoid !important;
            background-color: rgb(217, 221, 249) !important;
            color: #304FFF !important;
            font-weight: bold !important;
            text-align: right !important;
            padding: 6px 4px !important;
            border: 1px solid #ddd !important;
            font-size: 14px !important;
            -webkit-print-color-adjust: exact !important;
            color-adjust: exact !important;
            position: relative !important;
            z-index: 10 !important;
          }
          
          /* Ensure tbody doesn't interfere with thead */
          .monthly-report-modal .table tbody {
            display: table-row-group !important;
          }
          
          .monthly-report-modal .table tbody tr {
            display: table-row !important;
          }
          
          /* Additional rules to force header repetition */
          .monthly-report-modal .table thead {
            -webkit-print-color-adjust: exact !important;
            color-adjust: exact !important;
          }
          
          /* Footer container positioning for print */
          .monthly-report-modal .footer-container {
            position: relative !important;
            margin-top: 20px !important;
            page-break-inside: avoid !important;
            break-inside: avoid !important;
          }
          
          .monthly-report-modal .footer-table {
            width: 100% !important;
            border-collapse: collapse !important;
            margin-top: 30px !important;
          }
          
          .monthly-report-modal .footer-table .tr-footer {
            background: #e8e8e8 !important;
            border-top: 2px solid #000 !important;
            page-break-inside: avoid !important;
            break-inside: avoid !important;
            position: static !important;
          }
          
          .monthly-report-modal .footer-table .td-footer {
            background: rgb(251, 251, 251) !important;
            text-align: right !important;
            margin-left: 5px !important;
            font-weight: bold !important;
            padding: 8px 4px !important;
            border: 0.2px solid #e0e0e0 !important;
            -webkit-print-color-adjust: exact !important;
            color-adjust: exact !important;
          }
          
          /* Print media styles for table cells */
          .monthly-report-modal .td-description {
            font-weight: 200 !important;
            word-wrap: break-word !important;
            font-size: 8px !important;
            line-height: 1 !important;
            padding: 2px 1px !important;
          }
          
          .monthly-report-modal .td-amount {
            font-size: 8px !important;
            font-weight: 400 !important;
            font-family: Arial, sans-serif !important;
            line-height: 1 !important;
            padding: 2px 1px !important;
            text-align: right !important;
          }
          
          .monthly-report-modal .td-total {
            font-size: 8px !important;
            font-weight: bold !important;
            font-family: Arial, sans-serif !important;
            line-height: 1.1 !important;
            padding: 2px 1px !important;
            text-align: right !important;
          }
          
          /* Header cell sizing for print */
          .monthly-report-modal .table th {
            font-size: 9px !important;
            line-height: 1.1 !important;
            padding: 2px 1px !important;
          }
          
          /* Specific styling for SortingCode header */
          .monthly-report-modal .table th:first-child {
            font-size: 11px !important;
            padding: 6px 2px !important;
            text-align: right !important;
            word-wrap: break-word !important;
            white-space: nowrap !important;
            overflow: hidden !important;
            text-overflow: ellipsis !important;
          }
          
          /* Ensure footer table has proper column widths for print */
          .monthly-report-modal .footer-table {
            table-layout: fixed !important;
            direction: rtl !important;
          }
          

          
          /* Enhanced footer table styling for print */
          .monthly-report-modal .footer-table {
            table-layout: fixed !important;
            direction: rtl !important;
            border-collapse: collapse !important;
            width: 100% !important;
            max-width: 100% !important;
          }
          
          /* Footer table column widths for print */
          .monthly-report-modal .footer-table td:first-child {
            width: 90px !important; /* Match description column - further reduced */
            min-width: 90px !important;
            max-width: 90px !important;
          }
          
          .monthly-report-modal .footer-table td:not(:first-child):not(:last-child) {
            width: 70px !important; /* Match month columns - reduced for better fit */
            min-width: 70px !important;
            max-width: 70px !important;
          }
          
          .monthly-report-modal .footer-table td:last-child {
            width: 70px !important; /* Match total column - reduced for better fit */
            min-width: 70px !important;
            max-width: 70px !important;
          }
          
          .monthly-report-modal .footer-table .tr-footer {
            background: #e8e8e8 !important;
            border-top: 2px solid #000 !important;
            font-weight: bold !important;
          }
          
          .monthly-report-modal .footer-table .td-footer {
            background: rgb(251, 251, 251) !important;
            text-align: right !important;
            font-weight: bold !important;
            padding: 10px 6px !important;
            border: 1px solid #ddd !important;
            font-size: 12px !important;
            font-family: Arial, sans-serif !important;
            -webkit-print-color-adjust: exact !important;
            color-adjust: exact !important;
          }
          
 
      }
    `;
  };

  return (
    <Modal
      open={open}
      onClose={handleClose}
      aria-labelledby="monthly-report-modal-title"
    >
      <Box sx={{
        position: 'absolute',
        top: '50%',
        left: '50%',
        transform: 'translate(-50%, -50%)',
        width: monthlyReportHtml ? '75%' : 500,
        height: monthlyReportHtml ? '80%' : 'auto',
        bgcolor: 'background.paper',
        boxShadow: 24,
        borderRadius: '10px',
        p: 2,
      }}>
        {!monthlyReportHtml ? (
          <>
            <Typography variant="h6" component="h2" sx={{ mb: 1, textAlign: 'center' }}>
              {t('MonthlyProfitAndLossReport')}
            </Typography>

            <Grid dir={direction} container spacing={3}>
              {/* Year Selection */}
              <Grid item xs={12}>
                <Typography variant="subtitle1" sx={{ mb: 2, fontWeight: 'bold' }}>
                  {t('Year')}
                </Typography>
                <FormControl fullWidth sx={{ mb: 2 }}>
                  <InputLabel>שנה</InputLabel>
                  <Select
                    value={selectedYear}
                    onChange={(e) => setSelectedYear(e.target.value)}
                    label="שנה"
                  >
                    {Array.from({ length: 10 }, (_, i) => new Date().getFullYear() - i).map(year => (
                      <MenuItem dir={direction} key={year} value={year}>{year}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>

              {/* Start Month */}
              <Grid item xs={6}>
                <Typography variant="subtitle1" sx={{ mb: 2, fontWeight: 'bold' }}>
                  {t('StartMonth')}
                </Typography>
                <FormControl fullWidth sx={{ mb: 2 }}>
                  <InputLabel>מחודש</InputLabel>
                  <Select
                    value={startMonth}
                    onChange={(e) => setStartMonth(e.target.value)}
                    label="מחודש"
                  >
                    {Array.from({ length: 12 }, (_, i) => i + 1).map(month => (
                      <MenuItem dir={direction} key={month} value={month}>
                        {new Date(selectedYear, month - 1).toLocaleDateString('he-IL', { month: 'long' })}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>

              {/* End Month */}
              <Grid item xs={6}>
                <Typography variant="subtitle1" sx={{ mb: 2, fontWeight: 'bold' }}>
                  {t('EndMonth')}
                </Typography>
                <FormControl fullWidth>
                  <InputLabel>עד חודש</InputLabel>
                  <Select
                    value={endMonth}
                    onChange={(e) => setEndMonth(e.target.value)}
                    label="עד חודש"
                  >
                    {Array.from({ length: 12 }, (_, i) => i + 1).map(month => (
                      <MenuItem dir={direction} key={month} value={month}>
                        {new Date(selectedYear, month - 1).toLocaleDateString('he-IL', { month: 'long' })}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>

              {/* Date basis: Document date vs Registration month */}
              <Grid item xs={12}>
                <Box
                  sx={{
                    display: 'flex',
                    gap: '2px',
                    height: '32px',
                    padding: '1px',
                    borderRadius: '4px',
                    background: '#F8F9FA',
                    border: '1px solid #E9ECEF',
                    width: 'fit-content',
                  }}
                >
                  <Box
                    onClick={() => handleDateBasisChange(true)}
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      padding: '2px 12px',
                      borderRadius: '3px',
                      cursor: 'pointer',
                      backgroundColor: filterByDocumentDate ? '#304FFF' : 'transparent',
                      color: filterByDocumentDate ? '#FFFFFF' : '#6C757D',
                      fontWeight: filterByDocumentDate ? '600' : '500',
                      fontSize: '13px',
                      whiteSpace: 'nowrap',
                      transition: 'all 0.2s ease',
                    }}
                  >
                    {t('DocumentDate')}
                  </Box>
                  <Box
                    onClick={() => handleDateBasisChange(false)}
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      padding: '2px 12px',
                      borderRadius: '3px',
                      cursor: 'pointer',
                      backgroundColor: !filterByDocumentDate ? '#304FFF' : 'transparent',
                      color: !filterByDocumentDate ? '#FFFFFF' : '#6C757D',
                      fontWeight: !filterByDocumentDate ? '600' : '500',
                      fontSize: '13px',
                      whiteSpace: 'nowrap',
                      transition: 'all 0.2s ease',
                    }}
                  >
                    {t('RegistrationMonth')}
                  </Box>
                </Box>
              </Grid>

            </Grid>

            <Box dir={direction} sx={{ display: 'flex', justifyContent: 'space-between', mt: 4 }}>
              <Button
                variant="outlined"
                onClick={handleClose}
                sx={{ width: '120px' }}
              >
                {t('Cancel')}
              </Button>
              <Button
                variant="contained"
                onClick={handleGenerate}
                disabled={isGeneratingMonthlyReport}
                sx={{ width: '120px', backgroundColor: '#304FFF', '&:hover': { backgroundColor: '#304FFF' } }}
              >
                {isGeneratingMonthlyReport ? t('Loading') : t('GenerateReport')}
              </Button>
            </Box>
          </>
        ) : (
          <>
            {/* Report Display */}
            <Box dir={direction} sx={{ mb: 0.5, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>


                <Button
                  variant="contained"
                  onClick={handleDownloadExcel}
                  startIcon={<TableChartIcon />}
                  sx={{
                    width: '150px',
                    height: '35px',
                    padding: '4px 8px',
                    fontSize: '12px',
                    backgroundColor: '#304FFF',
                    color: '#fff',
                    '&:hover': { backgroundColor: '#F9AA2A' },
                  }}
                >
                  {t('DownloadExcelFile')}
                </Button>

                {/* PDF Download Button */}
                <Button
                  variant="contained"
                  onClick={handleDownloadPdf}
                  startIcon={<DownloadIcon />}
                  sx={{
                    width: '140px',
                    height: '35px',
                    padding: '4px 8px',
                    fontSize: '12px',
                    backgroundColor: '#304FFF',
                    color: '#fff',
                    '&:hover': { backgroundColor: '#F9AA2A' },
                  }}
                >
                  {t('DownloadPDFFile')}
                </Button>

                <Button
                  variant="contained"
                  onClick={() => {
                    if (cachedMonthlyData) {
                      const reportData = {
                        year: selectedYear,
                        startMonth: startMonth,
                        endMonth: endMonth
                      };
                      const printHtml = generatePrintVersion(reportData, cachedMonthlyData, showWithZeros);
                      const printWindow = window.open('', '_blank');
                      printWindow.document.write(printHtml);
                      printWindow.document.close();
                      printWindow.print();
                    }
                  }}
                  sx={{
                    width: '70px',
                    height: '35px',
                    padding: '4px 8px',
                    fontSize: '12px',
                    backgroundColor: '#304FFF',
                    color: '#fff',
                    '&:hover': { backgroundColor: '#F9AA2A' },
                  }}                >
                  {t('Print')}
                </Button>
                {/* Send to Chat Button */}
                {
                  userTypeContext?.toString() !== Constants.UserType.Client &&

                  <Tooltip title={t('SendToChat')}>
                    <IconButton
                      onClick={handleSendToChat}
                      sx={{
                        backgroundColor: '#4CAF50',
                        color: '#fff',
                        '&:hover': { backgroundColor: '#45a049' },
                        width: '35px',
                        height: '35px',
                        borderRadius: '8px',
                      }}
                    >
                      <svg xmlns="http://www.w3.org/2000/svg" height="24" width="24" viewBox="0 0 24 24" fill="currentColor">
                        <path d="M20 2H4C2.9 2 2 2.9 2 4V22L6 18H20C21.1 18 22 17.1 22 16V4C22 2.9 21.1 2 20 2ZM20 16H5.17L4 17.17V4H20V16ZM7 9H17V11H7V9ZM7 12H14V14H7V12Z" />
                      </svg>
                    </IconButton>
                  </Tooltip>
                }
                {/* Show With Zeros Checkbox - in the report display section */}
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={showWithZeros}
                      onChange={(e) => handleShowWithZerosChange(e.target.checked)}
                      color="primary"
                    />
                  }
                  label={t('ShowWithZeros')}
                  sx={{
                    '.MuiFormControlLabel-label': {
                      fontSize: '14px',
                      fontWeight: '500',
                    }
                  }}
                />
              </Box>


              <Box dir={direction} sx={{ mb: 0.5, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>

                <Button
                  variant="outlined"
                  onClick={handleClose}
                >
                  {t('Close')}
                </Button>
              </Box>
            </Box>

            {/* HTML Report Content - Using iframe to completely isolate styles */}
            <iframe
              srcDoc={monthlyReportHtml}
              style={{
                width: '100%',
                height: '70vh',
                border: '1px solid #ddd',
                borderRadius: '8px',
                backgroundColor: 'white'
              }}
              title="Monthly Report"
            />
          </>
        )}
      </Box>
    </Modal>
  );
};

export default MonthlyReportModal;